import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { ref, computed } from 'vue'

import liffapi from '@/liffapi'

function slugify(val) {
  return val.toLowerCase().replace(/[^\w-]/g, '-')
}

// Cache TTL constants
const CACHE_TTL_SHORT = 5 * 60 * 1000 // 5 minutes
const CACHE_TTL_LONG = 10 * 60 * 1000 // 10 minutes
const CACHE_CLEANUP_AGE = 24 * 60 * 60 * 1000 // 24 hours

function isCacheValid(lastFetched, ttl) {
  if (!lastFetched) return false
  return Date.now() - lastFetched < ttl
}

// IMPORTANT: When mapped, maps as this.event due to setMapStoreSuffix == ''
export const useEventStore = defineStore('event', () => {
  // ── Event key for scoped cache keys ──────────────────────────────
  // Drives all useStorage key scoping. Updated in setEvent().
  const _eventKey = ref(null)

  // Reactive cache key helper — useStorage switches localStorage slots
  // when the computed key changes (VueUse 14.x reactive key support)
  const _cacheKey = (suffix) =>
    computed(() =>
      _eventKey.value ? `event-${_eventKey.value}-${suffix}` : `event-_none-${suffix}`
    )

  // ── Tab-scoped state (sessionStorage) ────────────────────────────
  // entity and selectedLocation use sessionStorage so that each browser
  // tab has its own event context. Switching events in Tab 2 will NOT
  // affect Tab 1. Event-scoped caches below stay in localStorage so
  // same-event tabs share data via VueUse's StorageEvent listener.
  const entity = useStorage('event-entity', {}, sessionStorage)

  const inventoryCache = useStorage(_cacheKey('inventory'), {
    locks: [],
    locksLastFetched: null,
    lockerBanks: [],
    lockerBanksLastFetched: null,
  })

  const reservationsCache = useStorage(_cacheKey('reservations'), {
    reservations: [],
    reservationsLastFetched: null,
  })

  const lockersCache = useStorage(_cacheKey('lockers'), {
    lockers: [],
    lockersLastFetched: null,
    lockersLocation: null, // tracks which location this cache was fetched for
  })

  const mapsCache = useStorage(_cacheKey('maps'), {
    mapsList: [],
    mapsListLastFetched: null,
    mapsById: {},
    mapsLastFetchedById: {},
  })

  // Tab-scoped — different tabs may work different zones
  const selectedLocation = useStorage('event-selected-location', null, sessionStorage)

  // ── Non-persisted UI state ───────────────────────────────────────
  const cachedVersion = ref(0)
  const activeTab = ref('inventory')
  const inventorySubTab = ref('lockers')
  const lockSearchQuery = ref('')
  const reservationSearchQuery = ref('')
  const selectedReservationIds = ref([])

  const stats = ref({
    banks: 0,
    lockers_total: 0,
    lockers_available: 0,
    lockers_assigned: 0,
  })

  // ── Getters (computed) ──────────────────────────────────────────
  const locationLabel = computed(() => entity.value?.locationLabel)
  const endDate = computed(() => entity.value?.endDate)
  const startDate = computed(() => entity.value?.startDate)
  const branding = computed(() => entity.value?.branding)

  const displayName = computed(() => {
    let name = entity.value?.name
    return name || 'Account'
  })

  const isLoaded = computed(() => Boolean(entity.value?.key))
  const key = computed(() => entity.value?.key)
  const state = computed(() => entity.value?.state)
  const slug = computed(() => entity.value?.slug)

  // Inventory getters (from cache)
  const locks = computed(() => inventoryCache.value.locks)
  const locksLastFetched = computed(() => inventoryCache.value.locksLastFetched)
  const lockerBanks = computed(() => inventoryCache.value.lockerBanks)
  const lockerBanksLastFetched = computed(() => inventoryCache.value.lockerBanksLastFetched)

  // Individual locker entities (from cache)
  const lockers = computed(() => lockersCache.value.lockers)
  const lockersLastFetched = computed(() => lockersCache.value.lockersLastFetched)

  // Reservations getters (from cache)
  const reservations = computed(() => reservationsCache.value.reservations)
  const reservationsLastFetched = computed(() => reservationsCache.value.reservationsLastFetched)

  // Filtered locks based on search and location
  const filteredLocks = computed(() => {
    const needle = lockSearchQuery.value.trim().toLowerCase()
    const loc = selectedLocation.value
    return locks.value
      .filter((l) => !loc || l.location === loc)
      .filter((l) => !needle || String(l.number).toLowerCase().includes(needle))
  })

  const lockCount = computed(() => filteredLocks.value.length)

  // Filtered reservations based on search and location
  const filteredReservations = computed(() => {
    const needle = reservationSearchQuery.value.trim().toLowerCase()
    const loc = selectedLocation.value
    return reservations.value
      .filter((r) => !loc || r.location === loc)
      .filter(
        (r) =>
          !needle ||
          r.name?.toLowerCase().includes(needle) ||
          r.email?.toLowerCase().includes(needle)
      )
  })

  const reservationCount = computed(() => filteredReservations.value.length)

  // Available locks for assignment
  const availableLocks = computed(() => locks.value.filter((l) => l.state === 'available'))
  const availableLocksCount = computed(() => availableLocks.value.length)

  const locations = computed(() => entity.value?.locations || [])

  // Work zone getters
  const currentLocation = computed(() => {
    if (!selectedLocation.value) return null
    return locations.value.find((l) => l.id === selectedLocation.value)
  })

  // Maps getters
  const mapsList = computed(() => mapsCache.value.mapsList)
  const mapsListLastFetched = computed(() => mapsCache.value.mapsListLastFetched)

  const getMapById = (mapId) => {
    return mapsCache.value.mapsById[mapId] || null
  }

  const getMapByLocation = (location) => {
    const mapMetadata = mapsCache.value.mapsList.find((m) => m.location === location)
    if (!mapMetadata) return null
    return mapsCache.value.mapsById[mapMetadata.id] || null
  }

  const vendorKey = computed(() => entity.value?.vendorKey)

  // ── Actions ─────────────────────────────────────────────────────

  async function fetchLocks(options = {}) {
    const { force = false, state: lockState = null } = options

    if (!force && locks.value.length > 0 && isCacheValid(locksLastFetched.value, CACHE_TTL_SHORT)) {
      return locks.value
    }

    try {
      const response = await liffapi.locks.listByEvent(key.value, { state: lockState })
      const fetchedLocks = Array.isArray(response?.locks) ? response.locks : []

      inventoryCache.value = {
        ...inventoryCache.value,
        locks: fetchedLocks,
        locksLastFetched: Date.now(),
      }

      return locks.value
    } catch (error) {
      if (locks.value.length > 0) {
        console.warn('Using cached locks due to fetch failure')
        return locks.value
      }
      throw error
    }
  }

  async function fetchLockerBanks(options = {}) {
    const { force = false } = options

    if (
      !force &&
      lockerBanks.value.length > 0 &&
      isCacheValid(lockerBanksLastFetched.value, CACHE_TTL_SHORT)
    ) {
      return lockerBanks.value
    }

    try {
      const response = await liffapi.events.lockerBanksList(key.value)
      const banks = Array.isArray(response?.banks) ? response.banks : []

      inventoryCache.value = {
        ...inventoryCache.value,
        lockerBanks: banks,
        lockerBanksLastFetched: Date.now(),
      }

      updateStats()
      return lockerBanks.value
    } catch (error) {
      if (lockerBanks.value.length > 0) {
        console.warn('Using cached locker banks due to fetch failure')
        return lockerBanks.value
      }
      throw error
    }
  }

  async function fetchReservations(options = {}) {
    const { force = false } = options

    if (
      !force &&
      reservations.value.length > 0 &&
      isCacheValid(reservationsLastFetched.value, CACHE_TTL_SHORT)
    ) {
      return reservations.value
    }

    try {
      const response = await liffapi.reservations.list(key.value)
      const fetched = Array.isArray(response?.entities) ? response.entities : []

      reservationsCache.value = {
        reservations: fetched,
        reservationsLastFetched: Date.now(),
      }

      return reservations.value
    } catch (error) {
      if (reservations.value.length > 0) {
        console.warn('Using cached reservations due to fetch failure')
        return reservations.value
      }
      throw error
    }
  }

  async function fetchLockers(options = {}) {
    const { force = false, location = null } = options

    // Cache is valid only if TTL hasn't expired AND the cached location
    // matches what we're requesting. A null-location fetch ("all") doesn't
    // satisfy a location-scoped request, and vice versa.
    const locationMatch = lockersCache.value.lockersLocation === (location || null)

    if (
      !force &&
      locationMatch &&
      lockers.value.length > 0 &&
      isCacheValid(lockersLastFetched.value, CACHE_TTL_SHORT)
    ) {
      return lockers.value
    }

    try {
      const response = await liffapi.events.lockersList(key.value, location)
      const fetched = Array.isArray(response?.lockers) ? response.lockers : []

      lockersCache.value = {
        lockers: fetched,
        lockersLastFetched: Date.now(),
        lockersLocation: location || null,
      }

      return lockers.value
    } catch (error) {
      if (lockers.value.length > 0) {
        console.warn('Using cached lockers due to fetch failure')
        return lockers.value
      }
      throw error
    }
  }

  async function fetchLocations(options = {}) {
    const { force = false } = options

    if (!force && locations.value.length > 0) {
      return locations.value
    }

    try {
      const response = await liffapi.events.listLocations(key.value)
      entity.value.locations = response.locations

      return entity.value.locations
    } catch (error) {
      if (locations.value.length > 0) {
        console.warn('Using cached locations due to fetch failure')
        return locations.value
      }
      throw error
    }
  }

  async function fetchMapsList(options = {}) {
    const { force = false, silent = false } = options

    if (
      !force &&
      mapsList.value.length > 0 &&
      isCacheValid(mapsListLastFetched.value, CACHE_TTL_LONG)
    ) {
      return mapsList.value
    }

    try {
      const response = await liffapi.events.mapList(key.value)
      const maps = Array.isArray(response?.maps) ? response.maps : []

      mapsCache.value = {
        ...mapsCache.value,
        mapsList: maps,
        mapsListLastFetched: Date.now(),
      }

      return mapsList.value
    } catch (error) {
      if (!silent && mapsList.value.length > 0) {
        console.warn('Using cached maps list due to fetch failure')
        return mapsList.value
      }
      if (!silent) throw error
      return []
    }
  }

  async function fetchMap(mapId, options = {}) {
    const { force = false, silent = false } = options

    if (!force) {
      const cachedMap = getMapById(mapId)
      const lastFetched = mapsCache.value.mapsLastFetchedById[mapId]

      if (cachedMap && isCacheValid(lastFetched, CACHE_TTL_LONG)) {
        return cachedMap
      }
    }

    try {
      const response = await liffapi.events.mapGet(key.value, mapId)
      const map = response?.map

      if (!map) {
        throw new Error('Map not found in response')
      }

      mapsCache.value = {
        ...mapsCache.value,
        mapsById: {
          ...mapsCache.value.mapsById,
          [mapId]: map,
        },
        mapsLastFetchedById: {
          ...mapsCache.value.mapsLastFetchedById,
          [mapId]: Date.now(),
        },
      }

      return map
    } catch (error) {
      const cachedMap = getMapById(mapId)
      if (!silent && cachedMap) {
        console.warn(`Using cached map ${mapId} due to fetch failure`)
        return cachedMap
      }
      if (!silent) throw error
      return null
    }
  }

  async function fetchMapByLocation(location, options = {}) {
    const { force = false, silent = false } = options

    // Use cached maps list if available — it doesn't change mid-session.
    // Only fetch if we genuinely don't have it yet.
    if (mapsList.value.length === 0) {
      await fetchMapsList({ force: false, silent })
    }

    // Find map metadata for this location
    const mapMetadata = mapsList.value.find((m) => m.location === location)

    if (!mapMetadata) {
      const msg = `No map found for location: ${location}`
      if (!silent) throw new Error(msg)
      return null
    }

    // Fetch the full map data
    return await fetchMap(mapMetadata.id, { force, silent })
  }

  async function initializeWorkZone(force = false) {
    // Auto-select logic: if only 1 zone exists and none selected, select it
    if (locations.value.length === 1 && !selectedLocation.value) {
      selectedLocation.value = locations.value[0].id
      console.log('Auto-selected work zone:', locations.value[0].name)
    }
  }

  function updateStats() {
    stats.value.banks = lockerBanks.value.length
    stats.value.lockers_total = lockerBanks.value.reduce(
      (sum, bank) => sum + (bank.totalLockers || 0),
      0
    )
    // TODO: Calculate available/assigned from actual locker states
  }

  // Mutations for local updates
  function addLock(lock) {
    inventoryCache.value = {
      ...inventoryCache.value,
      locks: [lock, ...locks.value],
    }
  }

  function updateLock(lockKey, updates) {
    const idx = locks.value.findIndex((l) => l.key === lockKey)
    if (idx !== -1) {
      const updatedLocks = [...locks.value]
      updatedLocks[idx] = { ...updatedLocks[idx], ...updates }
      inventoryCache.value = {
        ...inventoryCache.value,
        locks: updatedLocks,
      }
    }
  }

  function addLockerBank(bank) {
    inventoryCache.value = {
      ...inventoryCache.value,
      lockerBanks: [...lockerBanks.value, bank],
    }
    updateStats()
  }

  function updateReservation(reservationKey, updates) {
    const idx = reservations.value.findIndex((r) => r.key === reservationKey)
    if (idx !== -1) {
      const updatedReservations = [...reservations.value]
      updatedReservations[idx] = { ...updatedReservations[idx], ...updates }
      reservationsCache.value = {
        ...reservationsCache.value,
        reservations: updatedReservations,
      }
    }
  }

  function updateLockerByKey(lockerKey, updates) {
    const idx = lockers.value.findIndex((l) => l.key === lockerKey)
    if (idx !== -1) {
      const updatedLockers = [...lockers.value]
      updatedLockers[idx] = { ...updatedLockers[idx], ...updates }
      lockersCache.value = {
        ...lockersCache.value,
        lockers: updatedLockers,
      }
    }
  }

  // UI state setters
  function setActiveTab(tab) {
    activeTab.value = tab
  }

  function setInventorySubTab(subTab) {
    inventorySubTab.value = subTab
  }

  function setSelectedLocation(locationId) {
    selectedLocation.value = locationId
  }

  function clearWorkZone() {
    selectedLocation.value = null
  }

  function setLockSearchQuery(query) {
    lockSearchQuery.value = query
  }

  function setReservationSearchQuery(query) {
    reservationSearchQuery.value = query
  }

  function clearReservationSelection() {
    selectedReservationIds.value = []
  }



  function clear() {
    entity.value = {}
    inventoryCache.value = {
      locks: [],
      locksLastFetched: null,
      lockerBanks: [],
      lockerBanksLastFetched: null,
    }
    reservationsCache.value = {
      reservations: [],
      reservationsLastFetched: null,
    }
    lockersCache.value = {
      lockers: [],
      lockersLastFetched: null,
    }
    mapsCache.value = {
      mapsList: [],
      mapsListLastFetched: null,
      mapsById: {},
      mapsLastFetchedById: {},
    }
    activeTab.value = 'inventory'
    inventorySubTab.value = 'lockers'
    selectedLocation.value = null
    lockSearchQuery.value = ''
    reservationSearchQuery.value = ''
    selectedReservationIds.value = []

    stats.value = {
      banks: 0,
      lockers_total: 0,
      lockers_available: 0,
      lockers_assigned: 0,
    }
  }

  function setEvent(event) {
    // Only clear if we have existing data for a different event.
    // Skip if entity is already empty (e.g. EventApp already called clear())
    if (event?.key && entity.value?.key && event.key !== entity.value.key) {
      clear()
    }

    // Update the event key FIRST — this switches all useStorage cache keys
    // to the new event's scoped localStorage slots
    _eventKey.value = event?.key || null
    entity.value = event

    // Lazily clean up stale caches from other events (non-blocking)
    setTimeout(cleanupOldCaches, 0)
  }

  /**
   * Clean up stale event caches from localStorage.
   * Purges event-scoped caches older than 24 hours.
   * Called lazily during setEvent() to prevent localStorage bloat.
   */
  function cleanupOldCaches() {
    const now = Date.now()
    const keysToRemove = []

    for (let i = 0; i < localStorage.length; i++) {
      const lsKey = localStorage.key(i)
      if (!lsKey || !lsKey.startsWith('event-')) continue

      // Purge legacy global keys that now live in sessionStorage
      // TODO: remove after 2026-04-01
      if (lsKey === 'event-entity' || lsKey === 'event-selected-location') {
        keysToRemove.push(lsKey)
        continue
      }
      // Skip keys for the current event
      if (_eventKey.value && lsKey.startsWith(`event-${_eventKey.value}-`)) continue

      try {
        const cached = JSON.parse(localStorage.getItem(lsKey))
        const timestamps = [
          cached?.locksLastFetched,
          cached?.lockerBanksLastFetched,
          cached?.reservationsLastFetched,
          cached?.lockersLastFetched,
          cached?.mapsListLastFetched,
        ].filter(Boolean)

        if (timestamps.length > 0) {
          const newestTs = Math.max(...timestamps)
          if (now - newestTs > CACHE_CLEANUP_AGE) {
            keysToRemove.push(lsKey)
          }
        }
      } catch {
        // Corrupt entry — remove it
        keysToRemove.push(lsKey)
      }
    }

    if (keysToRemove.length > 0) {
      console.debug(`Cleaning up ${keysToRemove.length} stale event cache(s)`)
      keysToRemove.forEach((k) => localStorage.removeItem(k))
    }
  }

  // ── Return public API ───────────────────────────────────────────
  // Pinia setup stores expose everything returned here.
  // mapStores, mapState, mapActions all work the same.
  return {
    // State
    entity,
    inventoryCache,
    reservationsCache,
    lockersCache,
    mapsCache,
    selectedLocation,
    cachedVersion,
    activeTab,
    inventorySubTab,
    lockSearchQuery,
    reservationSearchQuery,
    selectedReservationIds,
    stats,

    // Getters
    locationLabel,
    endDate,
    startDate,
    branding,
    displayName,
    isLoaded,
    key,
    state,
    slug,
    locks,
    locksLastFetched,
    lockerBanks,
    lockerBanksLastFetched,
    lockers,
    lockersLastFetched,
    reservations,
    reservationsLastFetched,
    filteredLocks,
    lockCount,
    filteredReservations,
    reservationCount,
    availableLocks,
    availableLocksCount,
    locations,
    currentLocation,
    mapsList,
    mapsListLastFetched,
    getMapById,
    getMapByLocation,
    vendorKey,

    // Actions
    fetchLocks,
    fetchLockerBanks,
    fetchReservations,
    fetchLockers,
    fetchLocations,
    fetchMapsList,
    fetchMap,
    fetchMapByLocation,
    initializeWorkZone,
    updateStats,
    addLock,
    updateLock,
    addLockerBank,
    updateReservation,
    updateLockerByKey,
    setActiveTab,
    setInventorySubTab,
    setSelectedLocation,
    clearWorkZone,
    setLockSearchQuery,
    setReservationSearchQuery,
    clearReservationSelection,
    clear,
    setEvent,
    cleanupOldCaches,
  }
})
